<template>
    <div class="favorite-modal-content">
        <div class="favorite-modal-content__actions">
            <SearchInput
                v-if="showSearchInput"
                :model-value="searchTerm"
                :placeholder="searchPlaceholder"
                class="favorite-modal-content__actions__search"
                data-testid="filter-favorite-lists-input"
                @debounced-update:model-value="search"
            />
            <CreateNewFreelancerListButton class="favorite-modal-content__actions__create" :profile="profile" />
        </div>

        <div class="favorite-modal-content__lists">
            <FavoriteList
                v-for="favoriteList in favoritesLists"
                :key="favoriteList.id"
                :freelancer="profile"
                :favorite-list="favoriteList"
                :freelancer-in-list="freelancerInFavoriteList(favoriteList)"
            />

            <div v-if="isLoading">
                <FavoriteListSkeleton />
                <FavoriteListSkeleton />
                <FavoriteListSkeleton />
            </div>

            <VJoyWrapper v-if="hasMoreResults" justify="center" no-margin class="joy-mt-4">
                <VJoyButton
                    class="allow-immersion"
                    variant="main"
                    size="small"
                    :disabled="isLoading"
                    @click="poolOfFreelancesStore.fetchMoreFreelancersLists()"
                >
                    {{ $t('customer.freelancer.pof.detail.view.more') }}
                </VJoyButton>
            </VJoyWrapper>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useTranslation} from '#imports';
    import type {DeepReadonly} from '@malt/types-utils';
    import {useDisplayMobile} from '@malt/utils';
    import {VJoyButton, VJoyWrapper} from '@maltjoy/core-vue';
    import type {FreelancersList} from '@api';
    import FavoriteList from './FavoriteList.vue';
    import FavoriteListSkeleton from './FavoriteListSkeleton.vue';
    import SearchInput from './SearchInput.vue';
    import {usePoolOfFreelancesStore} from '../../stores/pool-of-freelances.store';
    import CreateNewFreelancerListButton from '../create-freelancer-list/CreateFreelancerListButton.vue';
    import type {FreelancerProfile} from '../types';

    const props = defineProps<{
        profile: FreelancerProfile;
    }>();

    const poolOfFreelancesStore = usePoolOfFreelancesStore();
    const {t} = useTranslation();
    const {isDisplayMobile} = useDisplayMobile();

    const {isLoading, favoritesLists, hasMoreResults} = storeToRefs(poolOfFreelancesStore);

    const searchTerm = ref('');
    const searchPlaceholder = computed(() => {
        return isDisplayMobile ? t('customer.freelancer.pof.modal.action.search.list.mobile') : t('customer.freelancer.pof.modal.action.search.list');
    });
    const showSearchInput = computed(() => {
        return favoritesLists.value.length > 0 || searchTerm.value !== '';
    });

    function freelancerInFavoriteList(favoriteList: DeepReadonly<FreelancersList>): boolean {
        return poolOfFreelancesStore.isFreelancerInFavoriteList(favoriteList.id, props.profile.id);
    }

    function search(newSearchValue: string): void {
        poolOfFreelancesStore.fetchFreelancersLists(newSearchValue.trim().length === 0 ? undefined : newSearchValue);
    }

    poolOfFreelancesStore.fetchFreelancersLists();
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint' as joy;

    .favorite-modal-content {
        &__actions {
            display: flex;
            justify-content: space-between;

            &__search {
                max-width: 384px;
                margin: 0;
            }

            @include joy.screen_xs {
                flex-direction: column-reverse;
                align-items: flex-start;

                &__search {
                    width: 100%;
                }
            }
        }

        &__lists {
            overflow: auto;
        }
    }
</style>
